import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ProductCard from "@/components/ProductCard";
import { fetchMacbooks } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Laptop, Loader2 } from "lucide-react";

export default function ShopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [macbooks, setMacbooks] = useState([]);
  const [loading, setLoading] = useState(true);

  const categoryFilter = searchParams.get("category") || "all";
  const statusFilter = searchParams.get("status") || "all";

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (categoryFilter !== "all") params.category = categoryFilter;
    if (statusFilter !== "all") params.status = statusFilter;

    fetchMacbooks(params)
      .then((res) => setMacbooks(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [categoryFilter, statusFilter]);

  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === "all") {
      newParams.delete(key);
    } else {
      newParams.set(key, value);
    }
    setSearchParams(newParams);
  };

  const availableCount = macbooks.filter((m) => m.status === "available").length;
  const soldCount = macbooks.filter((m) => m.status === "sold").length;

  return (
    <div data-testid="shop-page">
      {/* Header */}
      <section className="hero-gradient py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h1 className="text-4xl sm:text-5xl font-bold text-[#1d1d1f] tracking-tight">
            Shop MacBooks
          </h1>
          <p className="mt-3 text-sm sm:text-base text-[#86868b]">
            Every MacBook is unique. Available items ship immediately.
          </p>
          <div className="flex items-center gap-3 mt-4">
            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs font-medium border">
              {availableCount} available
            </Badge>
            {soldCount > 0 && (
              <Badge className="bg-gray-100 text-[#86868b] border-gray-200 text-xs font-medium border">
                {soldCount} sold
              </Badge>
            )}
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-gray-100 bg-white sticky top-16 z-40" data-testid="filter-bar">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-4">
            <Select
              value={categoryFilter}
              onValueChange={(v) => updateFilter("category", v)}
            >
              <SelectTrigger
                className="w-[160px] rounded-full border-gray-200 text-sm"
                data-testid="filter-category"
              >
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Models</SelectItem>
                <SelectItem value="air">MacBook Air</SelectItem>
                <SelectItem value="pro">MacBook Pro</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={statusFilter}
              onValueChange={(v) => updateFilter("status", v)}
            >
              <SelectTrigger
                className="w-[160px] rounded-full border-gray-200 text-sm"
                data-testid="filter-status"
              >
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="available">Available</SelectItem>
                <SelectItem value="sold">Sold</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Product Grid */}
      <section className="py-10 sm:py-14">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {loading ? (
            <div className="flex items-center justify-center py-20" data-testid="shop-loading">
              <Loader2 className="w-6 h-6 animate-spin text-[#86868b]" />
            </div>
          ) : macbooks.length === 0 ? (
            <div className="text-center py-20" data-testid="shop-empty">
              <Laptop className="w-14 h-14 text-gray-300 mx-auto mb-4" />
              <h3 className="text-base font-semibold text-[#1d1d1f]">No MacBooks found</h3>
              <p className="mt-2 text-sm text-[#86868b]">
                Try adjusting your filters or check back soon.
              </p>
            </div>
          ) : (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
              data-testid="product-grid"
            >
              {macbooks.map((mb) => (
                <ProductCard key={mb.id} macbook={mb} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
